/**
 * Service Lines Configuration
 * Frontend configuration for the multi-line service architecture.
 * 
 * Lines can be:
 * - ACTIVE: Fully enabled and visible
 * - PREVIEW: Hidden but accessible with preview flag
 * - DISABLED: Not accessible at all
 */

export const LineStatus = {
  ACTIVE: 'active',
  PREVIEW: 'preview',
  DISABLED: 'disabled',
};

/**
 * Service Line Definitions
 * Each line represents a themed product family
 */
export const SERVICE_LINES = {
  // LINE 1: THE OUTLAW SERIES - Launch MVP
  outlaw: {
    lineId: 'outlaw',
    name: 'Wanted Posters',
    description: 'Western-themed wanted posters and outlaw experiences',
    status: LineStatus.ACTIVE,
    icon: '🤠',
    color: '#D4AF37',
    accentColor: '#B5952F',
    products: ['wanted_poster', 'outlaw_birthday', 'outlaw_tshirt'],
    scenes: ['outlaw_desk', 'saloon', 'sheriff_office'],
    routePrefix: '/outlaw',
    routes: ['/wanted-poster', '/outlaw-birthday'],
    launchPriority: 1,
    tagline: 'Become the Legend of the Wild West',
  },

  // LINE 2: THE WIZARDING WORLD - Future Release (DISABLED)
  wizard: {
    lineId: 'wizard',
    name: 'The Wizarding World',
    description: 'Magical coat of arms, spells, and enchanted experiences',
    status: LineStatus.DISABLED, // Hidden for MVP
    icon: '✨',
    color: '#7B68EE',
    accentColor: '#6A5ACD',
    products: ['wizard_coat_of_arms', 'magical_spells', 'wizard_tshirt'],
    scenes: ['wizard_study', 'potion_room', 'great_hall'],
    routePrefix: '/wizard',
    routes: ['/wizard-crest', '/magical-spells'],
    launchPriority: 2,
    tagline: 'Discover Your Magical Legacy',
  },

  // LINE 3: CELEBRATION CARDS - Active
  celebrations: {
    lineId: 'celebrations',
    name: 'Happy Moments & Group Cards',
    description: 'Group cards, birthday wishes, and celebration experiences',
    status: LineStatus.ACTIVE,
    icon: '🎉',
    color: '#EC4899',
    accentColor: '#DB2777',
    products: ['group_card', 'birthday_moment', 'anniversary_moment'],
    scenes: ['celebration_hall', 'birthday_party', 'romantic_garden'],
    routePrefix: '/celebrations',
    routes: ['/group-cards', '/moments'],
    launchPriority: 1,
    tagline: 'Celebrate Every Moment Together',
  },

  // LINE 4: FAMILY HERITAGE - Preview (for testing)
  heritage: {
    lineId: 'heritage',
    name: 'Coat of Arms',
    description: 'Classic coat of arms, family trees, and genealogy',
    status: LineStatus.ACTIVE, // Lead service line
    icon: '🏰',
    color: '#8B4513',
    accentColor: '#A0522D',
    products: ['classic_coat_of_arms', 'family_tree', 'heritage_book'],
    scenes: ['castle_hall', 'library', 'throne_room'],
    routePrefix: '/coat-of-arms',
    routes: ['/coat-of-arms', '/createarms', '/family-tree'],
    launchPriority: 2,
    tagline: 'Honor Your Ancestral Legacy',
  },

  // LINE 5: TIME CAPSULES - Active
  timecapsule: {
    lineId: 'timecapsule',
    name: 'The Timeless Vault',
    description: 'Time-locked gifts and future-dated experiences',
    status: LineStatus.ACTIVE,
    icon: '🔐',
    color: '#06B6D4',
    accentColor: '#0891B2',
    products: ['time_capsule', 'future_letter', 'milestone_reveal'],
    scenes: ['vault_door', 'treasure_chest', 'time_portal'],
    routePrefix: '/vault',
    routes: ['/time-capsule'],
    launchPriority: 1,
    tagline: 'Send a Gift Through Time',
  },
  // LINE 6: THE OFFICE LEGEND - B2B Employee Recognition (PREVIEW)
  office: {
    lineId: 'office',
    name: 'The Office Legend',
    description: 'B2B employee recognition: service awards, department legends, and work anniversaries',
    status: LineStatus.PREVIEW,
    icon: '🏆',
    color: '#0EA5E9',
    accentColor: '#0284C7',
    products: ['office_wanted_poster', 'office_coat_of_arms', 'office_legend_tshirt'],
    scenes: ['executive_office', 'hall_of_fame', 'awards_stage'],
    routePrefix: '/office-legends',
    routes: ['/office-legends', '/corporate'],
    launchPriority: 3,
    tagline: 'Recognition-as-a-Result for Your Team',
  },

  // LINE 7: CELEBRATION T-SHIRTS - Physical merch storefront
  tshirts: {
    lineId: 'tshirts',
    name: 'Celebration T-Shirts',
    description: 'Custom printed t-shirts: birthdays, milestones, vintage designs, and monogrammed keepsakes shipped via Printify',
    status: LineStatus.ACTIVE,
    icon: '👕',
    color: '#F97316',
    accentColor: '#EA580C',
    products: ['celebration_tshirt', 'vintage_year_tshirt', 'birthday_crew_tshirt'],
    scenes: ['tshirt_editor'],
    routePrefix: '/celebration-tshirts',
    routes: ['/celebration-tshirts'],
    launchPriority: 1,
    tagline: 'Wear the Story',
  },

  // LINE 8: BIRTHDAY & MILESTONE REMINDERS - Coming Soon
  // Foundation: will reuse the Timeless Capsule scheduler to auto-deliver a 3D reveal
  // on the recipient's birthday or milestone anniversary.
  reminders: {
    lineId: 'reminders',
    name: 'Birthday Reminders',
    description: 'Never miss a moment — schedule a Happy Ages 3D reveal to auto-deliver on the exact date. Powered by our Timeless Capsule scheduler.',
    status: LineStatus.DISABLED,
    icon: '📅',
    color: '#EC4899',
    accentColor: '#DB2777',
    products: ['birthday_reminder', 'milestone_reminder', 'recurring_reveal'],
    scenes: ['reminder_dashboard'],
    routePrefix: '/reminders',
    routes: ['/reminders'],
    launchPriority: 4,
    tagline: 'The Moment Delivers Itself',
  },
};

// Preview mode is enabled ONLY when the current URL literally contains
// ?preview=true. No sessionStorage / localStorage persistence — this
// eliminates any risk of leaking preview state to admins or real customers.
export const isPreviewModeEnabled = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('preview') === 'true';
};

export const exitPreviewMode = () => {
  // Clear both storages defensively in case an older build wrote to them.
  try {
    sessionStorage.removeItem('preview_mode');
    localStorage.removeItem('preview_mode');
  } catch {}
};

// Get all active service lines
export const getActiveLines = () => {
  return Object.values(SERVICE_LINES).filter(
    line => line.status === LineStatus.ACTIVE
  );
};

// Get all enabled lines (active + preview if enabled)
export const getEnabledLines = () => {
  const includePreview = isPreviewModeEnabled();
  
  return Object.values(SERVICE_LINES).filter(line => {
    if (line.status === LineStatus.ACTIVE) return true;
    if (line.status === LineStatus.PREVIEW && includePreview) return true;
    return false;
  });
};

// Check if a specific line is enabled
export const isLineEnabled = (lineId) => {
  const line = SERVICE_LINES[lineId];
  if (!line) return false;
  
  if (line.status === LineStatus.ACTIVE) return true;
  if (line.status === LineStatus.PREVIEW && isPreviewModeEnabled()) return true;
  return false;
};

// Get line by ID
export const getLineById = (lineId) => {
  return SERVICE_LINES[lineId] || null;
};

// Get line for a product
export const getLineForProduct = (productId) => {
  for (const line of Object.values(SERVICE_LINES)) {
    if (line.products.includes(productId)) {
      return line;
    }
  }
  return null;
};

// Get line for a route
export const getLineForRoute = (route) => {
  for (const line of Object.values(SERVICE_LINES)) {
    if (line.routes.some(r => route.startsWith(r))) {
      return line;
    }
  }
  return null;
};

// Get all enabled products
export const getEnabledProducts = () => {
  const products = [];
  for (const line of getEnabledLines()) {
    products.push(...line.products);
  }
  return products;
};

// Get line theme colors for styling
export const getLineTheme = (lineId) => {
  const line = SERVICE_LINES[lineId];
  if (!line) {
    return {
      primary: '#D4AF37',
      accent: '#B5952F',
      icon: '🎁',
    };
  }
  
  return {
    primary: line.color,
    accent: line.accentColor,
    icon: line.icon,
  };
};


/**
 * Route Guard - Check if route should be accessible
 */
export const shouldShowRoute = (route) => {
  // Always allow base routes
  const alwaysAllowed = ['/', '/gallery', '/about', '/contact', '/login', '/register'];
  if (alwaysAllowed.includes(route)) return true;
  
  // Check line-based routes
  const line = getLineForRoute(route);
  if (!line) return true; // Route not associated with a line
  
  return isLineEnabled(line.lineId);
};


export default SERVICE_LINES;
